import { useEffect, useRef, useState, type ReactNode } from 'react';
import './HelpTrigger.css';

export interface HelpTriggerProps {
  /** Content shown in the floating caption when the trigger is opened. */
  caption: ReactNode;
  /** Accessible label for the trigger button. Defaults to "Help". */
  label?: string;
}

const CAPTION_MAX_WIDTH = 360;
const GUTTER = 10;

/**
 * A small circular "?" button that toggles a floating caption.
 * Self-contained: owns its open state, caption positioning, and
 * outside-click dismissal.
 */
export function HelpTrigger({ caption, label = 'Help' }: HelpTriggerProps) {
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState<{ left: number; bottom: number } | null>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const captionRef = useRef<HTMLParagraphElement>(null);

  useEffect(() => {
    if (!open) return undefined;

    const updatePosition = () => {
      const btn = buttonRef.current;
      if (!btn) return;
      const rect = btn.getBoundingClientRect();
      // Sit the caption above the button with a small gutter.
      setPos({
        bottom: window.innerHeight - rect.top + GUTTER,
        left: Math.min(rect.left, window.innerWidth - CAPTION_MAX_WIDTH),
      });
    };

    const handlePointerDown = (event: PointerEvent) => {
      const target = event.target as Node | null;
      if (!target) return;
      if (buttonRef.current?.contains(target)) return;
      if (captionRef.current?.contains(target)) return;
      setOpen(false);
    };

    updatePosition();
    window.addEventListener('resize', updatePosition);
    window.addEventListener('scroll', updatePosition, true);
    document.addEventListener('pointerdown', handlePointerDown, true);
    return () => {
      window.removeEventListener('resize', updatePosition);
      window.removeEventListener('scroll', updatePosition, true);
      document.removeEventListener('pointerdown', handlePointerDown, true);
    };
  }, [open]);

  return (
    <>
      <button
        type="button"
        ref={buttonRef}
        className="alc-help-trigger"
        aria-label={label}
        aria-expanded={open}
        onClick={() => setOpen((prev) => !prev)}
      >
        ?
      </button>
      {open && pos && (
        <p
          ref={captionRef}
          className="alc-help-caption"
          role="note"
          style={{ bottom: pos.bottom, left: pos.left }}
        >
          {caption}
        </p>
      )}
    </>
  );
}
